import { useCallback, useMemo, useState } from "react";
import { DiffEditor, type DiffOnMount } from "@monaco-editor/react";

import CodeEditorLoadingSkeleton from "components/CodeEditor/LoadingSkeleton";
import DiffCounts from "components/DiffCounts";
import { getLanguageByFileName } from "components/CodeEditor/helpers";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import CollapsibleSection from "ds/components/CollapsibleSection";
import useTypedContext from "hooks/useTypedContext";
import { RepoFile } from "types/generated";
import useGetRepoFileHistory from "shared/SpaceliftVcsRepo/useGetRepoFileHistory";
import { ThemeContext } from "views/Theme";

import { decodeFileContent } from "../helpers";
import SpaceliftVcsRepoBinaryFilePlaceholder from "../BinaryFilePlaceholder";
import styles from "./styles.module.css";
import { computeDiffCounts, getEditorHeight } from "./helpers";
import { DIFF_EDITOR_OPTIONS } from "./constants";

type SpaceliftVcsRepoCommitFileSectionProps = {
  repoId: string;
  file: RepoFile;
  isCollapsed: boolean;
  onToggle: () => void;
};

const SpaceliftVcsRepoCommitFileSection = ({
  repoId,
  file,
  isCollapsed,
  onToggle,
  ref,
}: SpaceliftVcsRepoCommitFileSectionProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { currentFileVersion, previousFileVersion, loading } = useGetRepoFileHistory({
    repoId,
    filePath: file.filePath,
    skip: isCollapsed,
  });

  const currentDecoded = useMemo(
    () => decodeFileContent(currentFileVersion?.content?.content),
    [currentFileVersion?.content?.content]
  );

  const parentDecoded = useMemo(
    () => decodeFileContent(previousFileVersion?.content?.content),
    [previousFileVersion?.content?.content]
  );

  const currentContent = currentDecoded.content ?? "";
  const parentContent = parentDecoded.content;
  const isBinary = currentDecoded.isBinary || parentDecoded.isBinary;

  const [diffCounts, setDiffCounts] = useState({ additions: 0, deletions: 0 });

  const { isDarkMode, currentDarkModeSyntaxTheme, currentLightModeSyntaxTheme } =
    useTypedContext(ThemeContext);

  const theme = isDarkMode ? currentDarkModeSyntaxTheme : currentLightModeSyntaxTheme;

  const isNewFile = !file.isDeleted && parentContent === undefined;
  const isDeletedFile = file.isDeleted;

  const original = isNewFile ? "" : (parentContent ?? "");
  const modified = isDeletedFile ? "" : currentContent;
  const editorHeight = getEditorHeight(original, modified);

  const handleDiffEditorMount = useCallback<DiffOnMount>((editor) => {
    const updateCounts = () => {
      const changes = editor.getLineChanges();

      if (changes) {
        setDiffCounts(computeDiffCounts(changes));
      }
    };

    updateCounts();
    editor.onDidUpdateDiff(updateCounts);
  }, []);

  return (
    <div ref={ref}>
      <CollapsibleSection
        title={file.filePath}
        ariaLevel={3}
        ariaLabel={file.filePath}
        headerSuffix={
          <Box padding="large" direction="row" gap="medium" align="center" shrink="0">
            {isBinary && !loading && <Badge variant="gray" type="weak" text="Binary" />}
            {isDeletedFile && (
              <>
                <Badge variant="red" type="weak" text="Deleted" />
                {!isBinary && (
                  <DiffCounts
                    additions={0}
                    deletions={(parentContent ?? currentContent).split("\n").length}
                  />
                )}
              </>
            )}
            {isNewFile && (
              <>
                <Badge variant="green" type="weak" text="New" />
                {!isBinary && (
                  <DiffCounts additions={currentContent.split("\n").length} deletions={0} />
                )}
              </>
            )}
            {!isDeletedFile && !isNewFile && !isBinary && (
              <DiffCounts additions={diffCounts.additions} deletions={diffCounts.deletions} />
            )}
          </Box>
        }
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      >
        {loading && (
          <Box direction="column" padding="medium" fullWidth>
            <CodeEditorLoadingSkeleton />
          </Box>
        )}
        {!loading && isBinary && (
          <Box padding="medium">
            <SpaceliftVcsRepoBinaryFilePlaceholder
              fileType={currentDecoded.fileType ?? parentDecoded.fileType}
              bordered
            />
          </Box>
        )}
        {!loading && !isBinary && (
          <Box className={styles.editorWrapper} padding="medium" style={{ height: editorHeight }}>
            <DiffEditor
              original={original}
              modified={modified}
              language={getLanguageByFileName(file.filePath)}
              theme={theme}
              options={DIFF_EDITOR_OPTIONS}
              onMount={handleDiffEditorMount}
            />
          </Box>
        )}
      </CollapsibleSection>
    </div>
  );
};

export default SpaceliftVcsRepoCommitFileSection;
